import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Printer } from "lucide-react";

import { Tabs, DatePicker, DateRangePicker, MonthPicker, WeekPicker, Select, FilterField, FilterPanel, Button, Page, Alert, formatPageRangeSummary, type TabItem, type DateRangeValue } from "@marekl/adminer/components";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";

import { type Dayjs, parseApiDate, toApiDate } from "@marekl/adminer/datetime";
import { useOffsetPaginatedQuery } from "@marekl/adminer/hooks";
import { useVoucherTypes } from "../../lib/queries/voucherTypes";
import {
  useReportAggregate,
  useReportRides,
  downloadReport,
  downloadDailyReport,
  printDailyReport,
  type ReportRidesParams,
} from "../../lib/queries/reports";
import { businessToday } from "../../lib/businessDay";
import { AggregateContent, RidesContent } from "./ReportsContent";

import styles from "./ReportsPage.module.css";

type TabValue = "daily" | "weekly" | "monthly" | "custom" | "rides";

const TABS: TabItem[] = [
  { id: "daily", label: "Denní" },
  { id: "weekly", label: "Týdenní" },
  { id: "monthly", label: "Měsíční" },
  { id: "custom", label: "Vlastní" },
  { id: "rides", label: "Jízdy" },
];

type ReportRidesOffsetParams = ReportRidesParams & { enabled?: boolean };

function useReportRidesOffset({
  enabled = true,
  ...params
}: ReportRidesOffsetParams) {
  return useReportRides(params, enabled);
}

function dayRange(day: Dayjs): DateRangeValue {
  return { from: day.startOf("day"), to: day.startOf("day") };
}

function defaultRange(tab: TabValue): DateRangeValue | null {
  if (tab === "custom") return null;
  const today = businessToday();
  if (tab === "weekly") {
    return {
      from: today.startOf("isoWeek"),
      to: today.endOf("isoWeek").startOf("day"),
    };
  }
  if (tab === "monthly") {
    return {
      from: today.startOf("month"),
      to: today.endOf("month").startOf("day"),
    };
  }
  if (tab === "rides") {
    return {
      from: today.startOf("isoWeek"),
      to: today.endOf("isoWeek").startOf("day"),
    };
  }
  return dayRange(today);
}

function readTab(value: string | null): TabValue {
  if (
    value === "weekly" ||
    value === "monthly" ||
    value === "custom" ||
    value === "rides"
  )
    return value;
  return "daily";
}

function tabSwitchRange(tab: TabValue): DateRangeValue {
  return (
    defaultRange(tab) ?? dayRange(businessToday())
  );
}

export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = readTab(searchParams.get("tab"));
  const fromParam = searchParams.get("from") ?? "";
  const toParam = searchParams.get("to") ?? "";
  const typeParam = searchParams.get("type") ?? "";
  const sourceParam = searchParams.get("source") ?? "";

  const [fromDraft, setFromDraft] = useState<Dayjs | null>(() =>
    parseApiDate(fromParam),
  );
  const [toDraft, setToDraft] = useState<Dayjs | null>(() =>
    parseApiDate(toParam),
  );
  const [downloading, setDownloading] = useState<"csv" | "daily-pdf" | "print" | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Sync date drafts when URL changes (e.g., tab switch applying preset)
  useEffect(() => {
    setFromDraft(parseApiDate(fromParam));
    setToDraft(parseApiDate(toParam));
  }, [fromParam, toParam]);

  // Apply date preset when tab changes and no from/to in URL
  useEffect(() => {
    if (!fromParam || !toParam) {
      const preset = defaultRange(tab);
      if (preset) {
        const np = new URLSearchParams(searchParams);
        const from = toApiDate(preset.from);
        const to = toApiDate(preset.to);
        if (!from || !to) return;
        np.set("from", from);
        np.set("to", to);
        setSearchParams(np, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const { data: voucherTypes } = useVoucherTypes();

  const filterReady = !!fromParam && !!toParam;
  const commonParams = {
    from: fromParam,
    to: toParam,
    type: typeParam ? Number(typeParam) : undefined,
    source: sourceParam || undefined,
    granularity: tab === "daily" ? ("hourly" as const) : undefined,
  };

  const aggregateQuery = useReportAggregate(
    commonParams,
    filterReady && tab !== "rides",
  );
  const ridesQuery = useOffsetPaginatedQuery(
    useReportRidesOffset,
    { ...commonParams, enabled: filterReady && tab === "rides" },
    {
      searchParams,
      setSearchParams,
      defaultLimit: 50,
      limitOptions: [25, 50, 100, 200],
      summaryFormatter: (input) =>
        formatPageRangeSummary(input, {
          nounForms: ["záznam", "záznamy", "záznamů"],
        }),
    },
  );

  function handleTabChange(newTab: string) {
    const nextTab = readTab(newTab);
    const np = new URLSearchParams(searchParams);
    np.set("tab", nextTab);
    np.delete("page");
    const range = tabSwitchRange(nextTab);
    const from = toApiDate(range.from);
    const to = toApiDate(range.to);
    if (from && to) {
      np.set("from", from);
      np.set("to", to);
    }
    setSearchParams(np, { replace: true });
  }

  function handleRangeChange(next: DateRangeValue | null) {
    if (!next) {
      setFromDraft(null);
      setToDraft(null);
      const np = new URLSearchParams(searchParams);
      np.set("tab", tab);
      np.delete("from");
      np.delete("to");
      np.delete("page");
      setSearchParams(np, { replace: true });
      return;
    }

    const from = toApiDate(next.from);
    const to = toApiDate(next.to);
    if (!from || !to) return;
    setFromDraft(next.from);
    setToDraft(next.to);

    const np = new URLSearchParams(searchParams);
    np.set("tab", tab);
    np.set("from", from);
    np.set("to", to);
    np.delete("page");
    setSearchParams(np, { replace: true });
  }

  function handleTypeChange(value: string) {
    const np = new URLSearchParams(searchParams);
    if (value) np.set("type", value);
    else np.delete("type");
    np.delete("page");
    setSearchParams(np, { replace: true });
  }

  function handleSourceChange(value: string) {
    const np = new URLSearchParams(searchParams);
    if (value) np.set("source", value);
    else np.delete("source");
    np.delete("page");
    setSearchParams(np, { replace: true });
  }

  async function handleDownloadCSV() {
    if (!filterReady) return;
    setDownloading("csv");
    setDownloadError(null);
    try {
      await downloadReport("csv", tab === "rides" ? "rides" : "aggregate", commonParams);
    } catch {
      setDownloadError("Export se nezdařil. Zkuste to prosím znovu.");
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadDailyPDF() {
    if (!fromParam) return;
    setDownloading("daily-pdf");
    setDownloadError(null);
    try {
      await downloadDailyReport(fromParam);
    } catch {
      setDownloadError("Export se nezdařil. Zkuste to prosím znovu.");
    } finally {
      setDownloading(null);
    }
  }

  async function handlePrintDailyReport() {
    if (!fromParam) return;
    setDownloading("print");
    setDownloadError(null);
    try {
      await printDailyReport(fromParam);
    } catch {
      setDownloadError("Tisk se nezdařil. Zkuste to prosím znovu.");
    } finally {
      setDownloading(null);
    }
  }

  useSetTopbarCrumbs([{ label: "Reporty" }]);

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Reporty"
          subtitle="přehled prodejů voucherů a jízd v zvoleném období"
          subtitleVariant="tagline"
          actions={
            <>
              <Button
                size="sm"
                variant="default"
                onClick={handleDownloadCSV}
                disabled={!filterReady || downloading !== null}
                loading={downloading === "csv"}
              >
                <Download size={14} />
                Export CSV
              </Button>
              {tab === "daily" && (
                <>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleDownloadDailyPDF}
                    disabled={!fromParam || downloading !== null}
                    loading={downloading === "daily-pdf"}
                  >
                    <FileText size={14} />
                    Denní report
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handlePrintDailyReport}
                    disabled={!fromParam || downloading !== null}
                    loading={downloading === "print"}
                  >
                    <Printer size={14} />
                    Tisknout
                  </Button>
                </>
              )}
            </>
          }
        />

        {downloadError && (
          <Alert variant="err" className={styles.downloadError}>
            {downloadError}
          </Alert>
        )}

        <Tabs
          tabs={TABS}
          value={tab}
          onChange={handleTabChange}
          aria-label="Období reportu"
        />

        <FilterPanel className={styles.reportFilters}>
          {tab === "daily" && (
            <FilterField>
              <DatePicker
                label="Den"
                value={fromDraft}
                onChange={(next) =>
                  handleRangeChange(next ? dayRange(next) : null)
                }
                clearable={false}
              />
            </FilterField>
          )}
          {tab === "weekly" && (
            <FilterField>
              <WeekPicker
                label="Týden"
                value={
                  fromDraft && toDraft ? { from: fromDraft, to: toDraft } : null
                }
                onChange={handleRangeChange}
                clearable={false}
              />
            </FilterField>
          )}
          {tab === "monthly" && (
            <FilterField>
              <MonthPicker
                label="Měsíc"
                value={
                  fromDraft && toDraft ? { from: fromDraft, to: toDraft } : null
                }
                onChange={handleRangeChange}
                clearable={false}
              />
            </FilterField>
          )}
          {tab === "rides" && (
            <FilterField wide>
              <DateRangePicker
                label="Období"
                value={
                  fromDraft && toDraft ? { from: fromDraft, to: toDraft } : null
                }
                onChange={handleRangeChange}
              />
            </FilterField>
          )}
          {tab === "custom" && (
            <FilterField wide>
              <DateRangePicker
                label="Období"
                value={
                  fromDraft && toDraft ? { from: fromDraft, to: toDraft } : null
                }
                onChange={handleRangeChange}
              />
            </FilterField>
          )}
          <FilterField>
            <Select
              label="Typ voucheru"
              id="filter-type"
              value={typeParam}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              <option value="">Všechny typy</option>
              {voucherTypes?.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField>
            <Select
              label="Zdroj"
              id="filter-source"
              value={sourceParam}
              onChange={(e) => handleSourceChange(e.target.value)}
            >
              <option value="">Vše</option>
              <option value="counter">Přímý prodej</option>
              <option value="eshop">Certifikát</option>
            </Select>
          </FilterField>
        </FilterPanel>

        {tab !== "rides" && <AggregateContent query={aggregateQuery} />}
        {tab === "rides" && <RidesContent query={ridesQuery} />}
      </Page.Content>
    </>
  );
}
